import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalize phone number for backend storage (+84xxxxxxxxx E.164 format)
 * Frontend input: 0xxxxxxxxx -> Backend storage: +84xxxxxxxxx
 * @param phone - Phone number in various formats (0xxx, +84xxx, 84xxx)
 * @returns Phone number in +84xxxxxxxxx format (E.164)
 */
export function normalizePhoneNumber(phone: string): string {
  if (!phone?.trim()) return "";

  // Remove all spaces, dashes, parentheses
  const cleaned = phone.replaceAll(/[\s\-()]/g, "");

  // Handle different formats - always return +84xxxxxxxxx (E.164)
  if (cleaned.startsWith("+84")) {
    return cleaned; // Already in E.164 format: +84xxxxxxxxx
  } else if (cleaned.startsWith("84")) {
    return "+" + cleaned; // Add + prefix: 84xxxxxxxxx -> +84xxxxxxxxx
  } else if (cleaned.startsWith("0")) {
    return "+84" + cleaned.substring(1); // Replace 0 with +84: 0xxxxxxxxx -> +84xxxxxxxxx
  }

  // Default: assume it's a Vietnamese number without country code
  return "+84" + cleaned;
}

/**
 * Display phone number for frontend (0xxxxxxxxx format)
 * Backend storage: 84xxxxxxxxx -> Frontend display: 0xxxxxxxxx
 * @param phone - Phone number in backend format (84xxxxxxxxx)
 * @returns Phone number in 0xxxxxxxxx format for display
 */
export function displayPhoneNumber(phone: string): string {
  if (!phone?.trim()) return "";

  // Remove all spaces, dashes, parentheses
  const cleaned = phone.replaceAll(/[\s\-()]/g, "");
  if (!cleaned) return "";

  // Convert from backend format to display format
  if (cleaned.startsWith("+84")) {
    return "0" + cleaned.substring(3); // +84xxxxxxxxx -> 0xxxxxxxxx
  } else if (cleaned.startsWith("84")) {
    return "0" + cleaned.substring(2); // 84xxxxxxxxx -> 0xxxxxxxxx
  } else if (cleaned.startsWith("0")) {
    return cleaned; // Already in display format: 0xxxxxxxxx
  }

  // Default: assume it needs 0 prefix
  return "0" + cleaned;
}

export const formatPhoneNumber = (phone: string) => {
  const cleaned = phone.replaceAll(/[\s\-()]/g, "");
  const phonePattern = /^(\d{3,4})(\d{3})(\d{3,4})$/;
  const match = phonePattern.exec(cleaned);
  if (match) {
    return `${match[1]} ${match[2]} ${match[3]}`;
  }
  return phone;
};
